import {
  Box,
  BoxProps,
  Button,
  Center,
  Flex,
  FlexProps,
  Heading,
  Text,
  useClipboard,
  Image,
  Progress,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { use100vh } from 'react-div-100vh';
import { useParams } from 'react-router-dom';
import {
  useCartStore,
  useCheckoutFormValuesStore,
  useCheckoutStore,
} from '../../stores';
import ReactPixel from 'react-facebook-pixel';
import ReactGA4 from 'react-ga4';
import { useCountdown, useEventData, useEventHistory } from '../../hooks';
import { useToast } from 'src/hooks';
import { userApi } from 'src/api';
import { GetSellStatusResponse } from 'src/types/api';
import { ReactNode, useEffect, useState } from 'react';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { isCabofoliaEvent } from 'src/utils/featureFlags';
import { formatCurrency } from 'src/utils/formatters';
import { IoCopyOutline } from 'react-icons/io5';
import { createPortal } from 'react-dom';
import { IoIosClose } from 'react-icons/io';
import React from 'react';

type ContainerProps = FlexProps & {
  children: ReactNode;
};

const Container = ({ children, ...props }: ContainerProps) => (
  <Flex
    as="section"
    direction="column"
    align="center"
    w="100%"
    maxW="sm"
    {...props}>
    {children}
  </Flex>
);

type CardProps = BoxProps & {
  children: ReactNode;
};
const Card = ({ children, ...props }: CardProps) => (
  <Box
    boxShadow="container"
    borderRadius="xl"
    bg="white"
    p="6"
    w="100%"
    {...props}>
    {children}
  </Box>
);
const getSellStatus = (sellId: number) =>
  userApi
    .get<GetSellStatusResponse>(`sell/${sellId}/status`)
    .then(res => res.data);

const getSellStatusThrowable = async (sellId: number) => {
  const response = await getSellStatus(sellId);
  if (response === 'paymentCancelled') throw new Error('Pagamento cancelado');
  if (response === 'paymentWaitingConfirmation')
    throw new Error('Pagamento aguardando confirmação');
  return response;
};

export const CheckoutPix = () => {
  const { eventID } = useParams();
  const height = use100vh();

  const eventHistory = useEventHistory();
  const { clearEventCheckout } = useCheckoutStore();
  const {
    getEventCartTotal,
    reinitializeEventCart,
    getEventCartItemsAdded,
    getEventCartTotalTickets,
  } = useCartStore();

  const { formValues } = useCheckoutFormValuesStore();

  const event = useEventData();
  const [hasCopiedByButton, setHasCopiedByButton] = useState(false);
  const [hasCopiedByText, setHasCopiedByText] = useState(false);
  const [valuePurchased] = React.useState({
    price: getEventCartTotal(event?.id!),
    eventName: event?.name,
    items: getEventCartItemsAdded(event?.id!),
    quantityTotal: getEventCartTotalTickets(event?.id!),
    quantity: getEventCartTotalTickets(event?.id!),
  });
  const handleOnCopy = () => {
    onCopy();
    toast({ description: 'Código copiado' });
  };

  const { onCopy } = useClipboard(formValues.qrCode);
  const countdownInMilliseconds = useCountdown(
    formValues.expiresAtInMilliseconds ?? 0,
  );
  const toast = useToast();

  const { isSuccess } = useQuery({
    queryKey: ['sell/status', formValues.sellId],
    queryFn: () => getSellStatusThrowable(formValues.sellId),
    enabled:
      !!formValues.sellId &&
      countdownInMilliseconds > 0 &&
      countdownInMilliseconds + 1000 <
        (formValues.expiresAtInMilliseconds ?? 0) -
          (formValues.createdAtInMilliseconds ?? 0) -
          20 * 1000,
    retry: true,
    retryDelay: 10 * 1000,
  });

  useEffect(() => {
    if (event?.id && isSuccess) {
      // @ts-ignore
      window.dataLayer.push(valuePurchased);

      if (
        event?.integrations?.pixelId &&
        window.location.href.includes(event.id)
      ) {
        ReactPixel.trackSingle(event.integrations?.pixelId, 'Purchase', {
          content_type: 'product',
          content_ids: [event.id],
          value: valuePurchased.price,
          num_items: valuePurchased.quantityTotal,
          content_name: event.name,
          currency: 'BRL',
        });
      }

      const purchaseReactGA4 = {
        item_category: 'product',
        data: valuePurchased,
      };

      ReactGA4.event('purchase', {
        currency: 'BRL',
        value: valuePurchased.price,
        items: [purchaseReactGA4],
      });

      if (isCabofoliaEvent(event.id)) {
        // @ts-ignore
        function gtag() {
          // @ts-ignore
          window.dataLayer.push(arguments);
        }

        // @ts-ignore
        gtag('event', 'conversion', {
          send_to: 'AW-448238546/XFxMCPqCgvUBENKn3tUB',
          value: valuePurchased.price,
          currency: 'BRL',
        });
      }
    }
  }, [
    event?.id,
    event?.integrations?.pixelId,
    event?.name,
    getEventCartItemsAdded,
    getEventCartTotal,
    getEventCartTotalTickets,
    isSuccess,
    valuePurchased,
  ]);

  useEffect(() => {
    if (valuePurchased) {
      reinitializeEventCart(eventID!, event?.ticketTypes);
      clearEventCheckout(eventID!);
    }
  }, [
    clearEventCheckout,
    event?.ticketTypes,
    eventID,
    reinitializeEventCart,
    valuePurchased,
  ]);

  if (isSuccess) {
    eventHistory.replace(`/event/${eventID}/checkout/pix-success`);
  }

  if (countdownInMilliseconds <= 0 && !isSuccess) {
    eventHistory.replace(`/event/${eventID}/checkout/pix-rejected`);
  }

  return createPortal(
    <Flex
      align="center"
      overflowX="auto"
      flexDirection="column"
      position="fixed"
      top={0}
      bottom={0}
      left={0}
      right={0}
      zIndex="99999"
      bg="#EDEDED"
      textAlign="center"
      width="100vw"
      height={height ? `${height}px` : '100vh'}
      flex="1"
      p="5">
      <IconButton
        icon={<Icon as={IoIosClose} boxSize="10" color="black" />}
        onClick={() => eventHistory.replace(`/event/${eventID}`)}
        aria-label="close"
        cursor="pointer"
        zIndex="99"
        position="absolute"
        right="0"
        variant="outline"
        border="none"
        top="10px"
      />
      <Container maxW="xl">
        <Heading textAlign="center" as="h2" m="0" mt="10" mb="2">
          Aguardando Pagamento PIX
        </Heading>
        <Heading as="h3" color="gray.800" fontWeight="medium" m="0" mb="10">
          {`Pague em até ${formValues.expirationDifferenceInMinutes} min`}
        </Heading>
        <Card textAlign="center">
          <Box
            borderWidth="1px"
            borderColor="gray.100"
            borderRadius="md"
            px="4"
            py="3"
            mb="4">
            <Text mb="1">Valor do Pedido</Text>
            <Text fontSize="2xl">
              {formatCurrency(formValues.amount ? formValues.amount / 100 : 0)}
            </Text>
          </Box>
          <Text color="gray.500" mb="4">
            Escaneie o código QR ou Copie o código abaixo e utilize o Pix Copia
            e Cola no seu app de pagamento ou Internet banking
          </Text>
          <Button
            onClick={() => {
              handleOnCopy();
              setHasCopiedByText(true);
              setHasCopiedByButton(false);
            }}
            colorScheme="gray"
            backgroundColor="gray.50"
            w="full"
            py="6"
            borderRadius="xl"
            mb="8">
            <Text textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
              {formValues.qrCode}
            </Text>
            <Center ml="4" color="blue.500">
              <IoCopyOutline />
            </Center>
          </Button>
          {hasCopiedByText && (
            <Text
              mt="-4"
              mb="4"
              color="green.500"
              fontWeight="bold"
              fontSize="large">
              Código copiado com sucesso
            </Text>
          )}
          <Box mb="8">
            <Image
              boxSize={['200px', '200px', '250px', '250px']}
              objectFit="cover"
              src={formValues.qrCodeUrl}
            />
          </Box>
          <Box textAlign="left" mb="8">
            <Text color="gray.500" fontSize="xs" mb="2">
              O tempo para você pagar acaba em:
            </Text>
            <Text fontSize="2xl" fontWeight="bold" mb="2">
              {format(new Date(countdownInMilliseconds), 'mm:ss', {
                locale: ptBR,
              })}
            </Text>
            <Progress
              h="4px"
              value={
                (countdownInMilliseconds /
                  ((formValues.expiresAtInMilliseconds ?? 0) -
                    (formValues.createdAtInMilliseconds ?? 0))) *
                100
              }
              colorScheme="brand"
            />
          </Box>
          <Button
            onClick={() => {
              handleOnCopy();
              setHasCopiedByButton(true);
              setHasCopiedByText(false);
            }}
            w="full"
            colorScheme="blue"
            rounded="full">
            Copiar Código PIX
          </Button>
          {hasCopiedByButton && (
            <Text
              mt="4"
              mb="4"
              color="green.500"
              fontWeight="bold"
              fontSize="large">
              Código copiado com sucesso
            </Text>
          )}
        </Card>
      </Container>
    </Flex>,
    document.body,
  );
};
